import React, { FunctionComponent, SyntheticEvent } from 'react';
import classnames from 'classnames';

import { Attachment, isVideoType, PreviewType } from '@api/v4/attachments/attachmentTypes';
import { PrimaryButton } from '@components/library/button/index';
import { getFilenameSansExt, handleThumbnailError } from '@components/asset/modal/tabs/edit/helpers';
import AttachmentConversionOptionsDropdown from '@components/asset/modal/tabs/asset_details/asset_attachments/AttachmentConversionOptions';
import { keyPressHelper } from '@helpers/accessibility';
import formatNumber from '@helpers/format_number';
import EmbedAttachmentButton from '@components/asset/modal/tabs/shared/EmbedAttachmentButton';

interface AdditionalAttachmentFields {
  attachment_key: string;
  created_at: string;
  id: number;
  thumbnailed: boolean;
}

export type FullAttachment = Attachment & AdditionalAttachmentFields;

interface AssetAttachmentProps {
  assetKey: string;
  attachment: FullAttachment;
  attachmentConversionOptions: string[];
  colorSpace: string;
  displayAsProcessed: boolean;
  isConvertable: boolean;
  isDownloadable: boolean;
  presetSizes: number[];
  previewType: PreviewType;
  processed: boolean;
  restrictedDimensions: {
    width: { min: number; max: number };
    height: { min: number; max: number };
  };
  selectedAttachmentKey: string;
  setSelectedAttachmentKey: SetStateDispatch<string>;
}


const ThumbnailImage = (props: { extension: string; filename: string; url: string }): JSX.Element => (
  <div className="attachment-item--thumbnail">
    <img
      alt={props.filename}
      className="attachment-item--thumbnail__img"
      onError={(event: SyntheticEvent<HTMLImageElement, Event>): void => {
        handleThumbnailError(
          event,
          props.extension,
          getFilenameSansExt(props.filename, props.extension)
        );
      }}
      src={props.url}
    />
  </div>
);

const AssetAttachment: FunctionComponent<AssetAttachmentProps> = ({
  assetKey,
  attachment,
  attachmentConversionOptions,
  colorSpace,
  displayAsProcessed,
  isConvertable,
  isDownloadable,
  presetSizes,
  previewType,
  processed,
  restrictedDimensions,
  selectedAttachmentKey,
  setSelectedAttachmentKey
}) => {
  // TODO: eliminate snake case variables
  /* eslint-disable @typescript-eslint/camelcase */
  const {
    attachment_key,
    created_at,
    extension,
    filename,
    height,
    size,
    thumbnail_url,
    url,
    view_thumbnail_retina,
    width,
  } = attachment;

  const downloadLink = (): string => {
    if (!BFG.resource) return '';
    const digestParam = BFG.manifest ? `&manifest_digest=${BFG.manifest.digest}` : '';
    return `/${BFG.resource.slug}/attachments/${attachment_key}?dl=true&resource_key=${BFG.resource.key}&resource_type=${BFG.resource.type}${digestParam}`;
  };
  const trackAttachmentDownload = (): void => {
    const options = { resource_type: BFG.resource?.type, resource_key: BFG.resource?.key };
    Insight.createEvent('downloaded', assetKey, 'asset', options);
    if (BFG.manifest?.type === 'share_manifest') {
      // ensures that download event is tracked on share manifest
      Insight.createEvent('downloaded', assetKey, 'asset', { resource_type: 'share_manifest', resource_key: BFG.manifest.key });
    }
  };
  const handleDownload = (): void => {
    trackAttachmentDownload();
    window.location.href = downloadLink();
  };

  const renderEmbedAttachmentButton = (downloadAttachment = false): JSX.Element => {
    const displayUrl = isVideoType(previewType)
      ? url
      : (view_thumbnail_retina || thumbnail_url);

    return (
      <EmbedAttachmentButton
        attachmentKey={attachment_key}
        displayUrl={displayUrl}
        name={filename}
        url={url}
        downloadAttachment={downloadAttachment}
      />
    );
  };

  const visitAdminLink = (): void => {
    let host = 'brandfolder';
    if (BFG.getty_client === "true") {
      host = 'dam.gettyimages';
    }

    window.open(`https://admin.${host}.com/admin/attachments/${attachment_key}`, '_blank');
  };

  const createdAtString = new Intl.DateTimeFormat(BFG.locales.staticSiteLocale).format(new Date(created_at));
  const isEmbeddedContext = window.location.host.includes('embed');

  const renderDownloadButtons = (): JSX.Element => (isEmbeddedContext
    ? (
      <div className="asset-attachment__top-container--embed-buttons">
        {renderEmbedAttachmentButton()}
        {renderEmbedAttachmentButton(true)}
      </div>
    ) : (
      <div className="asset-attachment__top-container--buttons">
        {displayAsProcessed && (
          <AttachmentConversionOptionsDropdown
            assetKey={assetKey}
            attachmentKey={attachment_key}
            colorSpace={colorSpace}
            conversionOptions={attachmentConversionOptions}
            dimensions={{ height, width }}
            extension={extension}
            filename={filename}
            isConvertable={isConvertable}
            presetSizes={presetSizes}
            previewType={previewType}
            processed={processed}
            restrictedDimensions={restrictedDimensions}
          />
        )}
        <PrimaryButton
          className="attachment-download-button"
          data-testid="asset-attachment__download-button"
          icon="bff-download"
          onClick={(): void => {
            if (BFG.downloadAlertEnabled) {
              BF.fx.dispatchWindowEvent('launchDownloadAlert', null, {
                downloadCallback: handleDownload
              });
            } else {
              handleDownload();
            }
          }}
        />
      </div>
    )
  );

  return (
    <div
      className={classnames(
        "asset-attachment",
        { 'attachment-is-selected': attachment_key === selectedAttachmentKey }
      )}
      data-testid={`asset-attachment--${attachment_key}`}
      onClick={(): void => setSelectedAttachmentKey(attachment_key)}
      onKeyPress={keyPressHelper((): void => setSelectedAttachmentKey(attachment_key))}
      tabIndex={0}
      role="button"
    >
      <div className="asset-attachment__top-container">
        <ThumbnailImage
          extension={extension}
          filename={filename}
          url={thumbnail_url || `//static-fast.brandfolder.com/assets/types/${extension || 'file'}.svg`}
        />
        {isDownloadable && (extension !== "bftemplate") && renderDownloadButtons()}
      </div>
      <div className="asset-attachment__bottom-container">
        <div>
          <p className="asset-attachment__filename">
            {filename}
          </p>
        </div>
        <div className="asset-attachment__metadata-container">
          <span className="file-type-pill" data-testid="asset-attachment__file-type">
            {extension === 'bftemplate' ? <span className="bff-template-editor" /> : extension}
          </span>
          <span className="asset-attachment__metadata--item">{formatNumber(size)}</span>
          {width && height && (
            <span className="asset-attachment__metadata--item">
              <span className="asset-attachment__metadata--dot">&middot;</span>{width} x {height} px
            </span>
          )}
          {colorSpace && (
            <span className="asset-attachment__metadata--item">
              <span className="asset-attachment__metadata--dot">&middot;</span>{colorSpace.toUpperCase()}
            </span>
          )}
          <span className="asset-attachment__metadata--item">
            <span className="asset-attachment__metadata--dot">&middot;</span>{createdAtString}
          </span>
          {BFG.currentUser.su && (
            <span
              className="admin-dot-link"
              data-testid="asset-attachment__admin-link"
              onClick={visitAdminLink}
              onKeyPress={keyPressHelper(visitAdminLink)}
              role="button"
              tabIndex={0}
            >
              &middot;
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetAttachment;
